import type { Dispatch, SetStateAction } from 'react'
import { motion } from 'motion/react'
import type { ShopMessageKey } from '../../../i18n'
import { CustomerAreaSurfaceCard, customerMotionTransition } from '../customer-area'
import type { BillingFormState } from './preferencesShared'

type SaveState = 'idle' | 'saved' | 'error'

type BillingDataSectionProps = {
  billingFormState: BillingFormState
  setBillingFormState: Dispatch<SetStateAction<BillingFormState>>
  useForNextOrders: boolean
  setUseForNextOrders: Dispatch<SetStateAction<boolean>>
  touchedTaxCode: boolean
  setTouchedTaxCode: Dispatch<SetStateAction<boolean>>
  touchedBillingEmail: boolean
  setTouchedBillingEmail: Dispatch<SetStateAction<boolean>>
  taxCodeInvalid: boolean
  billingEmailInvalid: boolean
  billingError: string
  saveState: SaveState
  saving: boolean
  reducedMotion: boolean
  inputClassName: string
  surfaceCardClassName: string
  onSave: () => void
  t: (key: ShopMessageKey, params?: Record<string, string | number>) => string
}

export const BillingDataSection = ({
  billingFormState,
  setBillingFormState,
  useForNextOrders,
  setUseForNextOrders,
  touchedTaxCode,
  setTouchedTaxCode,
  touchedBillingEmail,
  setTouchedBillingEmail,
  taxCodeInvalid,
  billingEmailInvalid,
  billingError,
  saveState,
  saving,
  reducedMotion,
  inputClassName,
  surfaceCardClassName,
  onSave,
  t,
}: BillingDataSectionProps) => (
  <CustomerAreaSurfaceCard
    title={t('customer.preferences.billing.title')}
    subtitle={t('customer.preferences.billing.subtitle')}
    className={surfaceCardClassName}
    contentClassName="mt-4"
  >
    <div className="ca-card-shell ca-theme-transition rounded-xl px-5 py-5 sm:px-6 sm:py-6">
      <div className="grid gap-2.5 sm:grid-cols-2">
        <label className="grid gap-1 text-xs font-medium tracking-wide text-(--color-text-secondary)">
          {t('customer.preferences.billing.firstName')}
          <input
            value={billingFormState.firstName}
            onChange={(event) =>
              setBillingFormState((current) => ({
                ...current,
                firstName: event.target.value,
              }))
            }
            className={inputClassName}
          />
        </label>
        <label className="grid gap-1 text-xs font-medium tracking-wide text-(--color-text-secondary)">
          {t('customer.preferences.billing.lastName')}
          <input
            value={billingFormState.lastName}
            onChange={(event) =>
              setBillingFormState((current) => ({
                ...current,
                lastName: event.target.value,
              }))
            }
            className={inputClassName}
          />
        </label>
        <label className="grid gap-1 text-xs font-medium tracking-wide text-(--color-text-secondary)">
          {t('customer.preferences.billing.taxCode')}
          <input
            value={billingFormState.taxCode}
            onChange={(event) =>
              setBillingFormState((current) => ({
                ...current,
                taxCode: event.target.value,
              }))
            }
            onBlur={() => setTouchedTaxCode(true)}
            className={inputClassName}
          />
          {touchedTaxCode && taxCodeInvalid ? (
            <span className="text-xs text-(--color-status-error)">{t('customer.preferences.billing.taxCodeInvalid')}</span>
          ) : null}
        </label>
        <label className="grid gap-1 text-xs font-medium tracking-wide text-(--color-text-secondary)">
          {t('customer.preferences.billing.phone')}
          <input
            value={billingFormState.phone}
            onChange={(event) =>
              setBillingFormState((current) => ({
                ...current,
                phone: event.target.value,
              }))
            }
            className={inputClassName}
          />
        </label>
        <label className="grid gap-1 text-xs font-medium tracking-wide text-(--color-text-secondary) sm:col-span-2">
          {t('customer.preferences.billing.email')}
          <input
            value={billingFormState.billingEmail}
            onChange={(event) =>
              setBillingFormState((current) => ({
                ...current,
                billingEmail: event.target.value,
              }))
            }
            onBlur={() => setTouchedBillingEmail(true)}
            className={inputClassName}
          />
          {touchedBillingEmail && billingEmailInvalid ? (
            <span className="text-xs text-(--color-status-error)">{t('customer.preferences.billing.emailInvalid')}</span>
          ) : null}
        </label>
      </div>

      <label className="mt-3 inline-flex items-center gap-2 text-sm text-(--color-text-secondary)">
        <input
          type="checkbox"
          checked={useForNextOrders}
          onChange={(event) => setUseForNextOrders(event.target.checked)}
          className="h-4 w-4 rounded border-(--color-border-default) text-(--color-action-primary) focus:ring-(--color-focus-ring)"
        />
        <span>{t('customer.preferences.billing.useForNextOrders')}</span>
      </label>

      {billingError ? (
        <p className="mt-3 rounded-[10px] border border-(--color-status-error-border) bg-(--color-status-error-bg) px-3 py-2 text-sm text-(--color-status-error)">
          {billingError}
        </p>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <motion.button
          type="button"
          onClick={onSave}
          disabled={saving}
          whileTap={reducedMotion || saving ? undefined : { scale: 0.985 }}
          transition={customerMotionTransition}
          className="ca-btn-primary inline-flex h-9 items-center px-3.5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? (
            <>
              <svg className="mr-2 h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden>
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
                <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
              </svg>
              {t('customer.preferences.billing.saving')}
            </>
          ) : (
            t('customer.preferences.billing.save')
          )}
        </motion.button>
        <p className="text-xs text-(--color-text-muted)">{t('customer.preferences.billing.autoApplyHint')}</p>
        {saveState === 'saved' ? (
          <p className="text-sm text-(--color-status-success)">{t('customer.preferences.billing.saved')}</p>
        ) : null}
        {saveState === 'error' ? (
          <p className="text-sm text-(--color-status-error)">{t('customer.preferences.billing.saveError')}</p>
        ) : null}
      </div>
    </div>
  </CustomerAreaSurfaceCard>
)
